/* eslint-disable camelcase */
import React from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { CUSTOM_BRA_BAND_FILTER, CUSTOM_BRA_CUP_FILTER } from '../../constants';
import useBraSizeFilter from '../../hooks/useBraSizeFilter';
import useUnifiedProductGridData from '../../hooks/useUnifiedProductGridData';
import {
  isFacetOptionSelected,
  sanitizeFilterName,
  updateFiltersInUrlParams,
} from '../../utils/filters';
import { scrollToTopOfGrid } from '../../utils/scroll';
import CheckboxInput from '../SharedGridFiltersUi/CheckboxInput';

const DynamicFormattedMessage = FormattedMessage;

const SubFacetHeading = styled.h3`
  display: flex;
  padding-top: 0.5rem;
  ${({ theme }) => theme.gridFilterStyles.nestedHeadingStyle};
`;

const Fieldset = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-left: 0.75rem;
  border: none;
`;

const Legend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

function DesktopGridCustomBraFilter({ name, options = [] }) {
  const { selectedUrlFilters } = useUnifiedProductGridData();
  const router = useRouter();
  const { isMobile } = useBreakpoint();

  const {
    availableBandSizes,
    availableCupSizes,
    selectedBandSizeValue,
    setSelectedBandSizeValue,
  } = useBraSizeFilter({ options, facetName: name });

  const handleOnChange = ({ selectedCupSize }) => {
    const facetOption = `${selectedBandSizeValue}${selectedCupSize}`;

    updateFiltersInUrlParams({
      facetName: name,
      facetOption,
      router,
    });
    scrollToTopOfGrid({ isMobile });
  };

  // Both band sizes and cup sizes must be available to render the filter
  if (!availableBandSizes?.length || !availableCupSizes?.length) {
    return null;
  }

  return (
    <React.Fragment key={name}>
      <SubFacetHeading>
        <DynamicFormattedMessage
          id={`site_product_filter.${CUSTOM_BRA_BAND_FILTER.name}`}
          defaultMessage={CUSTOM_BRA_BAND_FILTER.displayName}
        />
      </SubFacetHeading>
      <Fieldset>
        <Legend>
          <DynamicFormattedMessage
            id={`site_product_filter.${CUSTOM_BRA_BAND_FILTER.name}`}
            defaultMessage={CUSTOM_BRA_BAND_FILTER.displayName}
          />
        </Legend>
        {availableBandSizes?.map(({ label, value }) => {
          return (
            <CheckboxInput
              key={value}
              id={`${name}-${value}`}
              name={value}
              label={label}
              checked={selectedBandSizeValue === value}
              onChange={() => {
                setSelectedBandSizeValue(value);
              }}
              filterName={sanitizeFilterName(CUSTOM_BRA_BAND_FILTER.name)}
            />
          );
        })}
      </Fieldset>
      <SubFacetHeading>
        <DynamicFormattedMessage
          id={`site_product_filter.${CUSTOM_BRA_CUP_FILTER.name}`}
          defaultMessage={CUSTOM_BRA_CUP_FILTER.displayName}
        />
      </SubFacetHeading>
      <Fieldset>
        <Legend>
          <DynamicFormattedMessage
            id={`site_product_filter.${CUSTOM_BRA_CUP_FILTER.name}`}
            defaultMessage={CUSTOM_BRA_CUP_FILTER.displayName}
          />
        </Legend>
        {availableCupSizes?.map(({ label, value }) => {
          const bandCupSizeValue = `${selectedBandSizeValue}${value}`;
          const isSelected = isFacetOptionSelected({
            facetName: name,
            facetOption: bandCupSizeValue,
            selectedUrlFilters,
          });
          return (
            <CheckboxInput
              key={value}
              id={`${name}-${value}`}
              name={value}
              label={label}
              superScriptLabel={selectedBandSizeValue}
              superScriptCategory="band-size"
              checked={isSelected}
              onChange={() => handleOnChange({ selectedCupSize: value })}
              filterName={sanitizeFilterName(CUSTOM_BRA_CUP_FILTER.name)}
            />
          );
        })}
      </Fieldset>
    </React.Fragment>
  );
}

DesktopGridCustomBraFilter.propTypes = {
  name: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
    })
  ),
};

export default DesktopGridCustomBraFilter;
